import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuthContext } from "@/features/auth/contexts/auth-context";
import { SystemConfiguration, getSystemConfigurations } from "../api/get-config-params";
import { SystemConfigurationUpsert, setSystemConfigurations } from "../api/set-config-params";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import { Permiso } from "@/shared/types/permiso";
import { notify } from "@/shared/notification";

export interface ParamsContextReturn {
  params: SystemConfiguration[];
  loading: boolean;
  error: Error | undefined;
  canEdit: boolean;
  refetch: () => Promise<void>;
  getBoolean: (key: string, defaultValue?: boolean) => boolean;
  getNumber: (key: string, defaultValue?: number) => number;
  getString: (key: string, defaultValue?: string) => string;
  getValue: (key: string) => SystemConfiguration | undefined;
  save: (entries: SystemConfigurationUpsert[]) => Promise<void>;
}

const ParamsContext = createContext<ParamsContextReturn>(null!);

export function useParamsContext() {
  return useContext(ParamsContext);
}

interface ParamsProviderProps extends PropsWithChildren {
  /**
   * ID de organización opcional:
   * - Sin organizacionId: usa el contexto del usuario actual (uso normal)
   * - Con organizacionId: obtiene parámetros de esa organización específica (solo admin)
   */
  organizacionId?: string;
}

export function ParamsProvider({ children, organizacionId }: ParamsProviderProps) {
  const { isLoggedIn } = useAuthContext();
  const hasManageParamsPermiso = useHasPermisos(Permiso.ManageParams);
  const [params, setParams] = useState<SystemConfiguration[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const refetch = useCallback(async () => {
    if (!isLoggedIn) return;
    setLoading(true);
    setError(undefined);
    try {
      const data = await getSystemConfigurations(organizacionId);
      setParams(data);
    } catch (err) {
      // Swallow 401/403 silently to avoid noise if backend restricts unexpectedly
      const e = err as any;
      const status = e?.response?.status;
      if (status !== 401 && status !== 403) setError(e as Error);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, organizacionId]);

  // Prevent double fetch in React StrictMode (dev) using a one-time guard
  const initRef = useRef(false);
  useEffect(() => {
    if (!isLoggedIn) return;
    if (initRef.current && !organizacionId) return; // Solo evita refetch para usuarios normales
    initRef.current = true;
    void refetch();
  }, [isLoggedIn, organizacionId, refetch]);

  const getBoolean = useCallback((key: string, defaultValue: boolean = false): boolean => {
    const entry = params.find(e => e.key === key);
    if (!entry) return defaultValue;
    return entry.stringValue.toLowerCase() === "true";
  }, [params]);

  const getNumber = useCallback((key: string, defaultValue: number = 0): number => {
    const entry = params.find(e => e.key === key);
    if (!entry) return defaultValue;
    const n = Number(entry.stringValue);
    return Number.isNaN(n) ? defaultValue : n;
  }, [params]);

  const getString = useCallback((key: string, defaultValue: string = ""): string => {
    const entry = params.find(e => e.key === key);
    return entry ? entry.stringValue : defaultValue;
  }, [params]);

  const getValue = useCallback((key: string): SystemConfiguration | undefined => {
    return params.find(e => e.key === key);
  }, [params]);

  const save = useCallback(async (upserts: SystemConfigurationUpsert[]) => {
    await setSystemConfigurations(upserts, organizacionId);
    await refetch();
    notify("Parámetros guardados correctamente");
  }, [refetch, organizacionId]);

  return (
    <ParamsContext.Provider value={{
      params,
      loading,
      error,
      canEdit: hasManageParamsPermiso,
      refetch,
      getBoolean,
      getNumber,
      getString,
      getValue,
      save,
    }}>
      {children}
    </ParamsContext.Provider>
  );
}
